import { createFileRoute } from '@tanstack/react-router';
import { FinanceReportsScreen } from '../../screens/finance/Reports';
import {
  isReportId,
  parseAccountsParam,
  type ReportId,
} from '../../screens/finance/report-filters';
import { isValidMonth } from '../../screens/finance/report-format';

/**
 * The Reports hub keeps its whole filter in the URL: which report, the month
 * range, and the account selection. That makes the filter shareable and the
 * back button meaningful, and it is what tells the screen the filter is global
 * rather than per-report. Everything is optional — the screen seeds missing
 * params from localStorage, then from defaults.
 */
export interface ReportsSearch {
  report?: ReportId;
  /** `YYYY-MM`. */
  from?: string;
  to?: string;
  /** Comma-joined account ids. Absent means all accounts. */
  accounts?: string;
}

export const Route = createFileRoute('/finance/reports')({
  component: FinanceReportsScreen,
  validateSearch: (raw: Record<string, unknown>): ReportsSearch => {
    const result: ReportsSearch = {};

    const report = raw['report'];
    if (isReportId(report)) result.report = report;

    // Both ends must be present and well-formed, or neither is honoured — a
    // lone `from` would pair with a defaulted `to` and silently widen the range.
    const from = raw['from'];
    const to = raw['to'];
    if (typeof from === 'string' && typeof to === 'string' && isValidMonth(from) && isValidMonth(to)) {
      result.from = from;
      result.to = to;
    }

    const accounts = parseAccountsParam(raw['accounts']);
    if (accounts !== undefined && accounts.length > 0) result.accounts = accounts.join(',');

    return result;
  },
});
